import { html, LitElement } from "lit";
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property } from 'lit/decorators.js';
import style from './usos-icon.scss';
import type { ElementInternals } from "element-internals-polyfill/dist/element-internals";

export type MaterialIconType = 'filled' | 'outlined' | 'round' | 'sharp' | 'two-tone';

/**
 * Provides access to icon assets with standardized tinting and positioning.
 */
@customElement('usos-icon')
export class UsosIcon extends LitElement {
    static override styles = [style];

    /**
     * Type of icon from Material Icons.
     */
    @property({attribute: 'icon-type'}) iconType: Optional<MaterialIconType> = null;

    /**
     * Name of icon from Material Icons.
     * If the name is specified as `<type>.<name>`, then the `<type>` Material Icons variant will be used.
     */
    @property({attribute: 'icon-name'}) iconName: string | null = null;

    /** URL to icon asset. Note that either this attribute or `icon-url` is required. */
    @property({attribute: 'icon-url'}) iconUrl: string | null = null;

    /**
     * Accessible label or empty if the icon should be hidden from accessibility tools.
     */
    @property() label = '';

    /**
     * Should this icon be treated as a button?
     */
    @property({type: Boolean, attribute: 'is-button', reflect: true}) isButton = false;

    private internals: ElementInternals;

    constructor() {
        super();
        this.internals = this.attachInternals();
    }

    override updated() {
        this.internals.role = this.isButton ? 'button' : '';
        this.internals.ariaLabel = this.label || '';
        this.internals.ariaHidden = this.label != '' ? 'false' : 'true';
        this.tabIndex = this.isButton ? 0 : -1;
    }

    override render() {
        const iconType = this.iconType ?? (this.iconName?.includes('.') && this.iconName.slice(0, this.iconName.indexOf('.'))) as Optional<MaterialIconType>;
        const iconName = this.iconName?.slice(this.iconName.indexOf('.') + 1);
        if (iconName) {
            const iconClass = {
                'material-icons': iconType == 'filled' || !iconType,
                'material-icons-outlined': iconType == 'outlined',
                'material-icons-round': iconType == 'round',
                'material-icons-sharp': iconType == 'sharp',
                'material-icons-two-tone': iconType == 'two-tone',
            }
            return html`
                <span id="icon-material" class=${classMap(iconClass)}
                    title=${this.label}
                >${iconName}</span>
            `;
        } else {
            return html`
                <span id='icon'
                    style="-webkit-mask-image: url(${this.iconUrl}); mask-image: url(${this.iconUrl})"
                    title=${this.label}
                ></span>
            `;
        }
    }

    override connectedCallback() {
        super.connectedCallback();
        this.addEventListener('keydown', this._onKeydown);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('keydown', this._onKeydown);
    }

    private _onKeydown(e: KeyboardEvent) {
        if (e.key == 'Enter') {
            e.stopPropagation();
            this.dispatchEvent(new Event('click'));
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
      "usos-icon": UsosIcon,
    }
}
